import { ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface PortalDropdownProps {
  isOpen: boolean
  onClose: () => void
  trigger: ReactNode
  children: ReactNode
  className?: string
}

export const PortalDropdown = ({
  isOpen,
  onClose,
  trigger,
  children,
  className = '',
}: PortalDropdownProps) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })
  const dropdownId = useRef(
    `portal-dropdown-${Math.random().toString(36).substr(2, 9)}`
  )

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // Dimensiones estimadas del dropdown (pueden ajustarse según el contenido)
      const dropdownWidth = 280 // Ancho mínimo del dropdown más ancho (fecha/importe)
      const dropdownHeight = 200 // Altura estimada

      let top = rect.bottom + window.scrollY
      let left = rect.left + window.scrollX

      // Ajustar posición horizontal si se sale del viewport
      if (left + dropdownWidth > viewportWidth) {
        // Posicionar desde el borde derecho del trigger hacia la izquierda
        left = rect.right + window.scrollX - dropdownWidth

        // Si aún se sale por la izquierda, posicionar en el borde derecho del viewport
        if (left < 0) {
          left = viewportWidth - dropdownWidth - 10 // 10px de margen
        }
      }

      // Ajustar posición vertical si se sale del viewport
      if (rect.bottom + dropdownHeight > viewportHeight) {
        // Posicionar encima del trigger
        top = rect.top + window.scrollY - dropdownHeight

        // Si aún se sale por arriba, mantener dentro del viewport
        if (top < window.scrollY) {
          top = window.scrollY + 10 // 10px de margen desde arriba
        }
      }

      setDropdownPosition({
        top,
        left,
        width: rect.width,
      })
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        // Check if click is inside the portal dropdown
        const portalDropdown = document.getElementById(dropdownId.current)
        if (portalDropdown && !portalDropdown.contains(event.target as Node)) {
          onClose()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  return (
    <>
      <div ref={triggerRef}>{trigger}</div>
      {isOpen &&
        createPortal(
          <div
            className={`fixed z-[9999] rounded-md border border-gray-200 bg-white shadow-lg ${className}`}
            id={dropdownId.current}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              minWidth: `${dropdownPosition.width}px`,
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </>
  )
}
